import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useContext } from 'react'
import { useForm } from 'react-hook-form'
import { isDeepEqual } from 'remeda'
import { useCurrentUserSetting } from '~/hooks'
import { RangeHistory } from '~/stores/types'

import { getValueAsArray } from '@hap/analytics'
import {
  Button,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  Form,
  FormFieldInput,
  Icon,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@hap/components'

import { FilterContext } from '../filter-context'
import {
  convertRangeFormToFilter,
  createRangeFilterSchema,
  defaultRangeFormatter,
} from './filter-values'

export function RangeFilterControl(props: {
  historyKey:
    | 'messageCountHistory'
    | 'durationHistory'
    | 'averageEvaluationScore'
    | 'escalationRiskScore'
    | 'holdCountHistory'
  field:
    | 'messageCount'
    | 'handleTime'
    | 'averageEvaluationScore'
    | 'escalationRiskScore'
    | 'totalHolds'
  valueFormatter?: (value: string | number) => string | number
  historyFormatter?: (value: string | number) => string | number
  // @deprecated use history formatter
  suffix?: string
  defaultHistory: RangeHistory[]
  maxLength?: number
  integerOnly?: boolean
}) {
  const historyFormatter = props.historyFormatter || defaultRangeFormatter
  const [history, setHistory] = useCurrentUserSetting(
    'lookup',
    props.historyKey,
    props.defaultHistory,
  )
  const { onSelect, filter, setOpen } = useContext(FilterContext)
  const values = getValueAsArray(filter)
  const form = useForm({
    resolver: zodResolver(createRangeFilterSchema(props.integerOnly)),
    values: {
      op: filter ? (filter.op as 'gte') : 'gte',
      start: values[0] ? Number(values[0]) : 0,
      end: values[1] ? Number(values[1]) : 0,
    },
  })

  const onSubmit = form.handleSubmit((data) => {
    if (data.op === 'between') {
      if (data.end <= data.start) {
        return form.setError('end', { message: 'End must be greater than start' })
      }
    }
    const parsedFilter = convertRangeFormToFilter(props.field, data, props.valueFormatter)
    const nextHistory: RangeHistory[] = [parsedFilter, ...history]
    setOpen(false)
    setHistory(
      nextHistory.reduce<RangeHistory[]>((acc, filter, index) => {
        if (index > 5) return acc
        if (acc.some((f) => isDeepEqual(filter, f))) return acc
        return [...acc, filter]
      }, []),
    )
    onSelect(parsedFilter)
  })

  const handleRemove = useCallback(
    (index: number) => {
      setHistory(history.filter((_, i) => i !== index))
    },
    [history],
  )

  const isBetween = form.watch('op') === 'between'

  return (
    <div>
      <Form {...form}>
        <form onSubmit={onSubmit}>
          <div className="grid gap-1 border-b p-2">
            <div className="flex items-center gap-1">
              <ToggleGroup
                value={form.watch('op')}
                type="single"
                onValueChange={(value) => {
                  form.setValue('op', value)
                }}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem value="gte" className="text-lg font-bold">
                      <Icon name="greater-than-or-equal" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>Greater than</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem value="lte" className="text-lg font-bold">
                      <Icon name="less-than-or-equal" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>Less than</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem value="between" className="text-xl font-bold">
                      <Icon name="arrows-horizontal" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>Between</TooltipContent>
                </Tooltip>
              </ToggleGroup>
              <FormFieldInput
                type="text"
                name="start"
                control={form.control}
                suffix={isBetween ? 'Start' : props.suffix}
                maxLength={3}
                inputClassName="h-auto"
                autoFocus
              />
              {isBetween && (
                <FormFieldInput
                  type="text"
                  name="end"
                  control={form.control}
                  maxLength={3}
                  suffix={isBetween ? 'End' : props.suffix}
                  inputClassName="h-auto"
                />
              )}
              <Button type="submit" icon="check" size="icon-sm" className="shrink-0" />
            </div>
          </div>
        </form>
      </Form>

      {history.length > 0 && (
        <Command>
          <CommandList>
            <CommandGroup heading="History">
              {history.map((filter, key) => (
                <CommandItem key={key} onSelect={() => onSelect(filter)}>
                  <span className="flex grow items-center gap-2">
                    {renderOperation(filter.op)}{' '}
                    {'values' in filter
                      ? filter.values.map(historyFormatter).join(' and ')
                      : historyFormatter(filter.value)}{' '}
                    {props.suffix}
                  </span>
                  <Button
                    icon="x"
                    size="icon-sm"
                    variant="ghost"
                    className="shrink-0 opacity-0 group-aria-selected:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemove(key)
                    }}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      )}
    </div>
  )
}

export function MessageFilterControl() {
  return (
    <RangeFilterControl
      historyKey="messageCountHistory"
      suffix="Messages"
      field="messageCount"
      defaultHistory={[
        { op: 'gte', value: `3`, field: 'messageCount' },
        { op: 'gte', value: `10`, field: 'messageCount' },
        { op: 'gte', value: `20`, field: 'messageCount' },
      ]}
    />
  )
}

export function renderOperation(op: string) {
  switch (op) {
    case 'gte':
      return <Icon size="12" name="greater-than-or-equal" />
    case 'lte':
      return <Icon size="12" name="less-than-or-equal" />
    case 'between':
      return <Icon size="12" name="arrows-horizontal" />
  }
}
